import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DashboardEvalFile } from '../../models/truth-management.model';

@Component({
  selector: 'app-file-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './file-selection-modal.component.html',
  styleUrl: './file-selection-modal.component.scss'
})
export class FileSelectionModalComponent implements OnInit {
  @Input() documentModel: string = '';
  @Input() files: DashboardEvalFile[] = [];
  @Input() isOpen: boolean = false;
  @Input() testRunMode: boolean = false;
  @Input() isLoadingFiles: boolean = false; // NEW: Loading state from parent
  
  @Output() onConfirm = new EventEmitter<string[]>();
  @Output() onCancel = new EventEmitter<void>();
  
  runType: 'full' | 'partial' = 'full';
  selectedFileIds: Set<string> = new Set();
  validationError: string = '';
  
  ngOnInit(): void {
     // If test run mode, force partial selection
    if (this.testRunMode) {
      this.runType = 'partial';
    }
  }
  
  // ... rest of the component methods stay the same
  
  /**
   * Toggle file selection (checkbox)
   */
  toggleFileSelection(fileId: string): void {
    if (this.selectedFileIds.has(fileId)) {
      this.selectedFileIds.delete(fileId);
    } else {
      this.selectedFileIds.add(fileId);
    }
    // Clear validation error when user makes a selection
    if (this.selectedFileIds.size > 0) {
      this.validationError = '';
    }
  }
  
  /**
   * Check if a file is selected
   */
  isFileSelected(fileId: string): boolean {
    return this.selectedFileIds.has(fileId);
  }
  
  /**
   * Handle run type change (radio button)
   */
  onRunTypeChange(): void {
    this.validationError = '';
    this.selectedFileIds.clear();
  }
  
  /**
   * Handle confirm button click
   */
  handleConfirm(): void {
    this.validationError = '';
    
    // In test run mode, must have files selected (cannot do full run)
    if (this.testRunMode) {
      if (this.selectedFileIds.size === 0) {
        this.validationError = 'Please select at least one file for test validation.';
        return;
      }
      
      const selectedIds = Array.from(this.selectedFileIds);
      // Log the actual file details for debugging
      const selectedFiles = this.files.filter(f => selectedIds.includes(f.uuid));
      selectedFiles.forEach(file => {
      });
      
      this.onConfirm.emit(selectedIds);
      this.closeModal();
      return;
    }
    
    // Normal mode - allow both full and partial
    if (this.runType === 'full') {
      // Full run - emit empty array to signal full document model run
      this.onConfirm.emit([]);
      this.closeModal();
    } else if (this.runType === 'partial') {
      // Partial run - validate that at least one file is selected
      if (this.selectedFileIds.size === 0) {
        this.validationError = 'Please select at least one file for validation.';
        return;
      }
      
      const selectedIds = Array.from(this.selectedFileIds);
      // Log the actual file details for debugging
      const selectedFiles = this.files.filter(f => selectedIds.includes(f.uuid));
      selectedFiles.forEach(file => {
      });
      
      this.onConfirm.emit(selectedIds);
      this.closeModal();
    }
  }
  
  /**
   * Handle cancel button click
   */
  handleCancel(): void {
    this.onCancel.emit();
    this.closeModal();
  }
  
  /**
   * Close modal and reset state
   */
  private closeModal(): void {
    this.isOpen = false;
    this.runType = 'full';
    this.selectedFileIds.clear();
    this.validationError = '';
  }
  
  /**
   * Get selected file count for display
   */
  get selectedCount(): number {
    return this.selectedFileIds.size;
  }
  
  /**
   * Get total file count for display
   */
  get totalCount(): number {
    return this.files.length;
  }
}
